import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signUp } from '../../auth/client.ts';
import { SignupLayout } from './Layout.tsx';

/**
 * Screen 2 — provider trio.
 *
 * Per spec §11.3.3 the order is Google primary, Apple second, email/password
 * expandable below. Google + Apple OAuth aren't provisioned at MVP launch
 * (env creds blank), so we render them as disabled buttons with a clear
 * "Coming soon" label rather than hiding them — the parent gets a stable
 * mental map of the providers we plan to support.
 *
 * Email/password path: better-auth's signUp.email() creates the auth.user
 * row; the user.create.after hook in apps/api/src/auth.ts then creates the
 * standalone.parent_account row in 'pending' state and triggers the
 * COPPA email-plus first-verification email.
 */
export function Step2Provider(): JSX.Element {
  const navigate = useNavigate();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onEmailSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await signUp.email({
        email,
        password,
        // The display name is collected on Step 4; a placeholder is fine
        // here. better-auth requires a name field for email/password.
        name: 'Parent',
      });
      if (res.error) {
        setError(res.error.message ?? 'Something went wrong. Try again?');
        return;
      }
      navigate('/signup/3');
    } catch {
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SignupLayout
      step={2}
      title="How would you like to sign in?"
      subtitle="Pick whatever's easiest. You can always switch later."
    >
      <div className="flex flex-col gap-3">
        <button
          type="button"
          disabled
          className="flex items-center justify-center gap-3 rounded-2xl border-2 border-slate-200 bg-white px-6 py-4 text-base font-medium text-slate-400 shadow-sm"
          title="Coming soon"
        >
          Continue with Google
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">Coming soon</span>
        </button>
        <button
          type="button"
          disabled
          className="flex items-center justify-center gap-3 rounded-2xl border-2 border-slate-200 bg-white px-6 py-4 text-base font-medium text-slate-400 shadow-sm"
          title="Coming soon"
        >
          Continue with Apple
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">Coming soon</span>
        </button>

        {!showEmailForm && (
          <button
            type="button"
            onClick={() => setShowEmailForm(true)}
            className="rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600"
          >
            Use email and password
          </button>
        )}

        {showEmailForm && (
          <form onSubmit={onEmailSubmit} className="flex flex-col gap-3">
            <label className="text-sm text-slate-700" htmlFor="email">
              Email
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </label>
            <label className="text-sm text-slate-700" htmlFor="password">
              Password (12+ characters)
              <div className="relative mt-1">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={12}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-xl border border-slate-300 px-4 py-3 pr-12 text-base focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 grid w-12 place-items-center text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? (
                    <svg
                      aria-hidden="true"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2"
                    >
                      <path
                        d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 5.1a10 10 0 0 1 11.5 6.9 10.1 10.1 0 0 1-2 3.5M6.5 6.7A10 10 0 0 0 2.6 12a10 10 0 0 0 14 5.4"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      aria-hidden="true"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2"
                    >
                      <path
                        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" />
                    </svg>
                  )}
                </button>
              </div>
            </label>
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Creating account…' : 'Continue'}
            </button>
          </form>
        )}
      </div>
    </SignupLayout>
  );
}
